import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, of as observableOf, Subject, Subscription } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { Division, Field, StorageBox } from '../../../core/store/models';
import { fieldSelectors, rootSelectors, storageBoxModalSelectors, storageBoxSelectors } from '../../../core/store/selectors';
import * as storageBoxModalActions from '../../../core/store/storage-box-modal/storage-box-modal.actions';
import * as warehouseLocationActions from '../../../core/store/warehouse-locations/warehouse-location.actions';
import { PDropdownOption } from '../../../shared/interfaces/p-dropdown-options';
import { FIELD_TYPES } from './../../../../../shared/fields/field-types';
import { STANDARD_FIELDS } from './../../../../../shared/fields/standard-fields';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import * as storageBoxActions from '../../../core/store/storage-boxes/storage-box.actions';
export const STORAGE_BOX_MODAL_TYPE = {
    CREATE: 'create',
    EDIT: 'edit'
};

export interface StorageBoxModalData {
    action: string;
    division: Division;
    warehouse_id: number;
    storageBox?: StorageBox;
}

@Component({
    templateUrl: './storage-box-modal.component.html',
    styleUrls: ['./storage-box-modal.component.scss'],
    standalone: false
})
export class StorageBoxModalComponent implements OnDestroy, OnInit {
    fields: Field[];
    fieldsObj: { [canonical_name: string]: Field } = {};
    fieldTypes = FIELD_TYPES;
    saveText = 'Save';
    spaceField: Field = new Field({
        id: -2,
        studio_id: null,
        canonical_name: 'space',
        name: 'Space?',
        type: FIELD_TYPES.CHECKBOX.type,
        required_locked: true,
        delete_locked: true,
        sort_order: null,
        picklistOptions: null,
        required: true
    });
    standardFields = STANDARD_FIELDS;
    storageBoxForm: UntypedFormGroup;
    STORAGE_BOX_MODAL_TYPE = STORAGE_BOX_MODAL_TYPE;
    titleText = 'Edit';
    warehouseLocationOptions$: Observable<PDropdownOption[]> = observableOf([]);
    warehouseSubLocationOptions$: Observable<PDropdownOption[]> = observableOf([]);
    @ViewChild('autosize') autosize!: CdkTextareaAutosize;
    private subs;
    private barcodeCheck$ = new Subject<void>();
    barcodeError: boolean;
    barcodeChecking: boolean;
    barcodeValid: boolean = true;  // true only when current barcode value is confirmed unique
    barcodeName = ''
    constructor(@Inject(MAT_DIALOG_DATA) private data, public dialogRef: MatDialogRef<StorageBoxModalComponent>, private fb: UntypedFormBuilder, private store: Store<rootSelectors.State>, public deviceService: DeviceDetectorService) { }

    ngOnInit() {
        if (this.data.action === STORAGE_BOX_MODAL_TYPE.CREATE) {
            this.titleText = 'Create';
            this.saveText = 'Create';
        }

        this.store.dispatch(new warehouseLocationActions.ListByStudioAction(this.data.division.studio_id));
        this.store.dispatch(new storageBoxActions.BarcodeResetAction());
        this.warehouseLocationOptions$ = this.store.select(storageBoxModalSelectors.getWarehouseLocationOptions);
        this.warehouseSubLocationOptions$ = this.store.select(storageBoxModalSelectors.getWarehouseSubLocationOptions);

        this.setupSubscriptions();

        this.dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
            this.store.dispatch(new storageBoxModalActions.SelectWarehouseLocationAction(null));
        });
    }

    ngOnDestroy() {
        if (this.subs) { this.subs.unsubscribe() };
        this.barcodeCheck$.next();
        this.barcodeCheck$.complete();
    }

    buildForm() {
        const formObj = {};

        if (this.data.storageBox && this.data.storageBox[STANDARD_FIELDS.warehouseLocation.canonical_name]) {
            this.store.dispatch(new storageBoxModalActions.SelectWarehouseLocationAction(this.data.storageBox[STANDARD_FIELDS.warehouseLocation.canonical_name]));
        }

        this.fields.concat(this.spaceField).forEach(field => {
            let initial = field.canonical_name === this.spaceField.canonical_name ? false : '';
            const validators = field.getValidators([STANDARD_FIELDS.name.canonical_name, STANDARD_FIELDS.warehouseLocation.canonical_name].includes(field.canonical_name));

            if (this.data.storageBox) {
                initial = this.data.storageBox[field.canonical_name];
            }

            formObj[field.canonical_name] = [initial, validators];
        });

        this.storageBoxForm = this.fb.group(formObj);
    }

    cancel() {
        this.dialogRef.close();
    }

    getFieldDisplayName(canonical_name: string) {
        return this.fieldsObj[canonical_name]
            ? this.fieldsObj[canonical_name].name
            : STANDARD_FIELDS[canonical_name].name;
    }

    onChange(val: any) {
        const trimmed = val?.trim();

        // Cancel any in-flight barcode check subscription
        this.barcodeCheck$.next();

        if (!trimmed) {
            this.barcodeError = false;
            this.barcodeChecking = false;
            this.barcodeValid = true;  // empty barcode is allowed — field is not mandatory
            this.barcodeName = '';
            return;
        }

        this.barcodeError = false;
        this.barcodeChecking = true;
        this.store.dispatch(new storageBoxActions.CheckBarcodeAction({ barcode: trimmed, id: this.data.action === STORAGE_BOX_MODAL_TYPE.CREATE ? null : this.data.storageBox.id, type: this.data.action, division_id: this.data.division.id }));

        this.store.select(storageBoxSelectors.getStorageBoxBarcodeState).pipe(filter(v => v !== null), take(1), takeUntil(this.barcodeCheck$)).subscribe((data) => {
            this.barcodeError = data;
            this.barcodeChecking = false;
            this.barcodeName = trimmed;
        });
    }
    setupSubscriptions() {
        if (this.subs) {
            throw new Error('Subscriptions already setup');
        }

        const storageBoxModalFieldsSub = this.store.select(fieldSelectors.getStorageBoxModalFields).subscribe(storageBoxModalFields => {
            const index = storageBoxModalFields.findIndex(f => f.canonical_name === 'name');
            if (index >= 0) {
                storageBoxModalFields[index] = new Field({ ...storageBoxModalFields[index] });
                storageBoxModalFields[index].name = 'Name';
            }
            this.fields = storageBoxModalFields;
            this.buildForm();
            this.fields.forEach(field => {
                this.fieldsObj[field.canonical_name] = field;
            });
        });

        this.subs = new Subscription();
        this.subs
            .add(storageBoxModalFieldsSub);
    }

    selectWarehouseLocation(warehouseLocationId: number) {
        this.store.dispatch(new storageBoxModalActions.SelectWarehouseLocationAction(warehouseLocationId));
        this.storageBoxForm.patchValue({ warehouse_sub_location_id: null });
    }

    submit() {
        const values = this.storageBoxForm.getRawValue();

        if (this.storageBoxForm.invalid) {
            return;
        }

        // If a barcode check is already in-flight, wait for it to complete
        if (this.barcodeChecking) {
            return;
        }

        const barcodeValue = values[STANDARD_FIELDS.barcode.canonical_name]?.trim();

        // Empty barcode is always allowed — field is not mandatory, skip uniqueness check
        if (!barcodeValue) {
            this.closeWithResult(values);
            return;
        }

        // In EDIT mode the barcode field may be pre-filled but onChange was never called.
        // Run the uniqueness check now before proceeding.
        const originalBarcode = this.data.storageBox?.[STANDARD_FIELDS.barcode.canonical_name]?.trim();
        const barcodeUnchecked = barcodeValue && this.barcodeName !== barcodeValue;
        const barcodeChangedInEdit = this.data.action === STORAGE_BOX_MODAL_TYPE.EDIT && barcodeValue !== originalBarcode;

        if (barcodeUnchecked || barcodeChangedInEdit) {
            this.onChange(barcodeValue);

            // Wait for the in-flight check to complete, then re-submit
            this.store.select(storageBoxSelectors.getStorageBoxBarcodeState)
                .pipe(filter(v => v !== null), take(1), takeUntil(this.barcodeCheck$))
                .subscribe(() => {
                    if (!this.barcodeError) {
                        this.closeWithResult(values);
                    }
                });
            return;
        }

        if (this.barcodeError) {
            return;
        }

        this.closeWithResult(values);
    }

    private closeWithResult(values: any) {
        let newStorageBox: StorageBox;

        if (this.data.action === STORAGE_BOX_MODAL_TYPE.CREATE) {
            newStorageBox = new StorageBox({
                division_id: this.data.division.id,
                warehouse_id: this.data.warehouse_id,
                ...values
            });
        } else {
            newStorageBox = new StorageBox({
                ...this.data.storageBox,
                ...values
            });
        }

        this.dialogRef.close(newStorageBox);
    }
}
